import type { i18n } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mapLangToLocale } from '#utils/formatCurrency'

const setupDateFormatters = (locale: string) => {
  return {
    numericDateTime: new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }),
    numericDate: new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }),
    shortMonthFormat: new Intl.DateTimeFormat(locale, {
      month: 'short',
    }),
  }
}

const formatterCache: Record<string, ReturnType<typeof setupDateFormatters>> = {
  'en-US': setupDateFormatters('en-US'),
  'de-DE': setupDateFormatters('de-DE'),
  'fr-FR': setupDateFormatters('fr-FR'),
  'it-IT': setupDateFormatters('it-IT'),
}

export const formatDateTime = (i18n: i18n, date: Date | Timestamp | undefined) => {
  const locale = mapLangToLocale[i18n.language] ?? 'en-US'
  const formatter = formatterCache[locale] ?? setupDateFormatters(locale)
  formatterCache[locale] = formatter
  return formatter.numericDateTime.format(date)
}

export const formatDate = (i18n: i18n, date: Date | Timestamp | undefined) => {
  const locale = mapLangToLocale[i18n.language] ?? 'en-US'
  const formatter = formatterCache[locale] ?? setupDateFormatters(locale)
  formatterCache[locale] = formatter
  return formatter.numericDate.format(date)
}

export const formatShortMonth = (i18n: i18n, date: Date | Timestamp | undefined) => {
  const locale = mapLangToLocale[i18n.language] ?? 'en-US'
  const formatter = formatterCache[locale] ?? setupDateFormatters(locale)
  formatterCache[locale] = formatter
  return formatter.shortMonthFormat.format(date)
}

export const useDateFormatter = () => {
  const { i18n } = useTranslation('shared')
  const locale = mapLangToLocale[i18n.language] ?? 'en-US'

  const formatter = formatterCache[locale] ?? setupDateFormatters(locale)
  formatterCache[locale] = formatter

  return useMemo(() => {
    return {
      numericDateTime: (date: Date | Timestamp) => {
        return formatter.numericDateTime.format(date)
      },
      numericDate: (date: Date | Timestamp) => {
        return formatter.numericDate.format(date)
      },
      shortMonth: (date: Date | Timestamp) => {
        return formatter.shortMonthFormat.format(date)
      },
    }
  }, [formatter])
}
